import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWind, faChartBar, faClock } from '@fortawesome/free-solid-svg-icons';
import { getBackendUrl } from '../../../services/configService';

// Simplified windrose generation for 24-hour data
interface HourlyWindroseData {
    direction: string;
    count: number;
    percentage: number;
    speedRanges: {
        range: string;
        count: number;
        color: string;
    }[];
}

interface HourlyWeatherRecord {
    date: string;
    wind_speed: number | null;
    wind_direction: number | null;
}

interface HourlyWindroseProps {
    stationId: string;
    stationName?: string;
    hours?: number;
    width?: number;
    height?: number;
}

// Wind speed color mapping (same as 10-year windrose)
const SPEED_RANGES = [
    { min: 0, max: 5, color: '#00FFFF', label: '0-5 km/h' },
    { min: 6, max: 10, color: '#0000FF', label: '6-10 km/h' },
    { min: 11, max: 15, color: '#00FF00', label: '11-15 km/h' },
    { min: 16, max: 20, color: '#FFFF00', label: '16-20 km/h' },
    { min: 21, max: 25, color: '#FFA500', label: '21-25 km/h' },
    { min: 26, max: 999, color: '#FF0000', label: '>25 km/h' }
];

// 16-point compass directions
const DIRECTIONS = [
    'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
    'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'
];

export const HourlyWindrose: React.FC<HourlyWindroseProps> = ({
    stationId,
    stationName,
    hours = 24,
    width = 400,
    height = 400
}) => {
    const [windroseData, setWindroseData] = useState<HourlyWindroseData[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [metadata, setMetadata] = useState<any>(null);
    // The record count and average live on data.summary, NOT data.metadata.
    // Reading them off metadata rendered both blank for every station.
    const [summary, setSummary] = useState<any>(null);
    const canvasRef = React.useRef<HTMLCanvasElement>(null);

    const getDirectionFromDegrees = (degrees: number): string => {
        const normalized = ((degrees % 360) + 360) % 360;
        const index = Math.round(normalized / 22.5) % 16;
        return DIRECTIONS[index];
    };

    const getSpeedRange = (speed: number) => {
        return SPEED_RANGES.find(range => speed >= range.min && speed <= range.max) || SPEED_RANGES[0];
    };

    const generateSimpleWindrose = (records: HourlyWeatherRecord[]): HourlyWindroseData[] => {
        console.log('🔄 generateSimpleWindrose called with records:', records?.length || 0);

        const directionData: { [key: string]: { total: number; speedBuckets: { [key: string]: number } } } = {};

        // Initialize direction data
        DIRECTIONS.forEach(dir => {
            directionData[dir] = { total: 0, speedBuckets: {} };
            SPEED_RANGES.forEach(range => {
                directionData[dir].speedBuckets[range.label] = 0;
            });
        });

        // Process records
        let validRecords = 0;
        let invalidRecords = 0;

        records.forEach((record, index) => {
            if (record.wind_direction !== null && record.wind_speed !== null) {
                const direction = getDirectionFromDegrees(record.wind_direction);
                const speedRange = getSpeedRange(record.wind_speed);

                // Log first few records for debugging
                if (index < 5) {
                    console.log(`🔍 Record ${index}:`, {
                        wind_direction: record.wind_direction,
                        wind_speed: record.wind_speed,
                        mapped_direction: direction,
                        speed_range: speedRange.label
                    });
                }

                directionData[direction].total++;
                directionData[direction].speedBuckets[speedRange.label]++;
                validRecords++;
            } else {
                invalidRecords++;
                // Log invalid records for debugging
                if (invalidRecords <= 3) {
                    console.log(`❌ Invalid record ${index}:`, {
                        wind_direction: record.wind_direction,
                        wind_speed: record.wind_speed
                    });
                }
            }
        });

        console.log('📊 Processing summary:', {
            totalRecords: records.length,
            validRecords,
            invalidRecords,
            directionCounts: Object.fromEntries(
                Object.entries(directionData).map(([dir, data]) => [dir, data.total])
            )
        });

        // Convert to windrose data
        const finalData = DIRECTIONS.map(direction => {
            const data = directionData[direction];
            const percentage = validRecords > 0 ? (data.total / validRecords) * 100 : 0;

            return {
                direction,
                count: data.total,
                percentage,
                speedRanges: SPEED_RANGES.map(range => ({
                    range: range.label,
                    count: data.speedBuckets[range.label],
                    color: range.color
                }))
            };
        });

        console.log('🎯 Final windrose data:', finalData);
        console.log('📊 Non-zero directions:', finalData.filter(d => d.count > 0));

        return finalData;
    };

    const renderSVGWindrose = (): string => {
        console.log('🎨 Rendering SVG windrose with data:', windroseData.length, 'directions');
        console.log('📏 Canvas size:', { width, height });

        const centerX = width / 2;
        const centerY = height / 2;
        const maxRadius = Math.min(width, height) * 0.35;

        // Find max percentage for scaling
        const maxPercentage = Math.max(...windroseData.map(d => d.percentage), 10);
        console.log('📊 Max percentage for scaling:', maxPercentage);

        let svg = `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`;

        // Background circle
        svg += `<circle cx="${centerX}" cy="${centerY}" r="${maxRadius}" fill="none" stroke="#ddd" stroke-width="1"/>`;

        // Grid circles
        for (let i = 1; i <= 4; i++) {
            const radius = (maxRadius * i) / 4;
            const percentage = (maxPercentage * i) / 4;
            svg += `<circle cx="${centerX}" cy="${centerY}" r="${radius}" fill="none" stroke="#eee" stroke-width="0.5"/>`;
            svg += `<text x="${centerX + radius + 5}" y="${centerY + 3}" font-size="10" fill="#999">${percentage.toFixed(1)}%</text>`;
        }

        // Direction bars
        windroseData.forEach((data, index) => {
            const angle = (index * 22.5 - 90) * (Math.PI / 180); // -90 to start from top (N)
            const barLength = (data.percentage / maxPercentage) * maxRadius;

            if (barLength > 0) {
                const x1 = centerX;
                const y1 = centerY;
                const x2 = centerX + Math.cos(angle) * barLength;
                const y2 = centerY + Math.sin(angle) * barLength;

                // Use the most common speed range color, or default blue
                const dominantSpeedRange = data.speedRanges.reduce((prev, current) =>
                    prev.count > current.count ? prev : current
                );
                const color = dominantSpeedRange.count > 0 ? dominantSpeedRange.color : '#0000FF';

                svg += `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${color}" stroke-width="4" opacity="0.8"/>`;
            }

            // Direction labels
            const labelRadius = maxRadius + 20;
            const labelX = centerX + Math.cos(angle) * labelRadius;
            const labelY = centerY + Math.sin(angle) * labelRadius + 4;
            svg += `<text x="${labelX}" y="${labelY}" font-size="11" font-weight="bold" text-anchor="middle" fill="#333">${data.direction}</text>`;
        });

        // Center dot
        svg += `<circle cx="${centerX}" cy="${centerY}" r="3" fill="#333"/>`;

        // Title
        svg += `<text x="${centerX}" y="20" font-size="14" font-weight="bold" text-anchor="middle" fill="#333">${stationName || stationId} - Last ${hours}h</text>`;

        svg += '</svg>';
        return svg;
    };

    // Canvas-based windrose rendering (matches windrose-generator.ts style)
    const renderCanvasWindrose = (): void => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const maxRadius = Math.min(canvas.width, canvas.height) / 2 - 80;

        // Find max percentage for scaling
        const maxPercentage = Math.max(...windroseData.map(d => d.percentage), 10);

        // Draw concentric circles (grid)
        ctx.strokeStyle = '#ddd';
        ctx.lineWidth = 1;

        for (let i = 1; i <= 4; i++) {
            const radius = (maxRadius / 4) * i;
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
            ctx.stroke();
        }

        // Draw percentage labels
        ctx.fillStyle = '#666';
        ctx.font = '12px Arial';
        ctx.textAlign = 'right';
        for (let i = 1; i <= 4; i++) {
            const radius = (maxRadius / 4) * i;
            const percentage = ((maxPercentage / 4) * i).toFixed(1);
            ctx.fillText(percentage + '%', centerX + radius + 5, centerY - 5);
        }

        // Draw direction lines and labels
        ctx.strokeStyle = '#ccc';
        ctx.lineWidth = 1;

        DIRECTIONS.forEach((dir, index) => {
            const angle = (index * 22.5 - 90) * Math.PI / 180;
            const x1 = centerX;
            const y1 = centerY;
            const x2 = centerX + Math.cos(angle) * maxRadius;
            const y2 = centerY + Math.sin(angle) * maxRadius;

            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();

            // Direction labels
            const labelRadius = maxRadius + 20;
            const labelX = centerX + Math.cos(angle) * labelRadius;
            const labelY = centerY + Math.sin(angle) * labelRadius;

            ctx.fillStyle = '#333';
            ctx.font = 'bold 12px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(dir, labelX, labelY);
        });

        // Draw windrose data as arc segments
        windroseData.forEach((data, index) => {
            if (data.count > 0) {
                const angle = (index * 22.5 - 90) * Math.PI / 180;
                const sectorWidth = 22.5 * Math.PI / 180;
                const startAngle = angle - sectorWidth / 2;
                const endAngle = angle + sectorWidth / 2;

                let cumulativeRadius = 0;

                // Draw each speed range as a stacked segment
                data.speedRanges.forEach(speedRange => {
                    if (speedRange.count > 0) {
                        const segmentPercentage = (speedRange.count / data.count) * data.percentage;
                        const segmentHeight = (segmentPercentage / maxPercentage) * maxRadius;

                        // Draw arc segment
                        ctx.fillStyle = speedRange.color;
                        ctx.beginPath();
                        ctx.arc(centerX, centerY, cumulativeRadius, startAngle, endAngle);
                        ctx.arc(centerX, centerY, cumulativeRadius + segmentHeight, endAngle, startAngle, true);
                        ctx.closePath();
                        ctx.fill();

                        // Draw border
                        ctx.strokeStyle = '#333';
                        ctx.lineWidth = 0.5;
                        ctx.stroke();

                        cumulativeRadius += segmentHeight;
                    }
                });

                // Draw total percentage label at the end of bar
                if (cumulativeRadius > 0) {
                    const labelAngle = angle;
                    const labelX = centerX + Math.cos(labelAngle) * (cumulativeRadius + 15);
                    const labelY = centerY + Math.sin(labelAngle) * (cumulativeRadius + 15);

                    ctx.fillStyle = '#000';
                    ctx.font = '10px Arial';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(data.percentage.toFixed(1) + '%', labelX, labelY);
                }
            }
        });

        // Draw center dot
        ctx.fillStyle = '#333';
        ctx.beginPath();
        ctx.arc(centerX, centerY, 3, 0, Math.PI * 2);
        ctx.fill();
    };

    const loadHourlyData = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${getBackendUrl()}/api/windrose/hourly-data`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    stationId,
                    stationName,
                    hours
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            console.log('🌬️ HourlyWindrose API Response:', result);

            if (!result.success) {
                throw new Error(result.error || 'API request failed');
            }

            console.log('🌬️ Raw hourly records count:', result.data.hourly_records?.length || 0);
            console.log('🌬️ Sample hourly records:', result.data.hourly_records?.slice(0, 3));

            const processedData = generateSimpleWindrose(result.data.hourly_records);
            console.log('🌬️ Processed windrose data:', processedData);

            setWindroseData(processedData);
            setMetadata(result.data.metadata);
            setSummary(result.data.summary);

        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load hourly data');
            console.error('24-Hour Windrose error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (stationId) {
            loadHourlyData();
        }
    }, [stationId, hours]);

    // Render canvas when windrose data changes
    useEffect(() => {
        if (windroseData.length > 0) {
            renderCanvasWindrose();
        }
    }, [windroseData]);

    if (loading) {
        return (
            <div className="windrose-container" style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="windrose-loading">
                    <div className="loading-spinner" style={{
                        width: '32px',
                        height: '32px',
                        border: '3px solid #f3f3f3',
                        borderTop: '3px solid #3498db',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <p style={{ marginTop: '12px', color: '#666' }}>Loading {hours}h windrose...</p>
                </div>
                <style dangerouslySetInnerHTML={{
                    __html: `
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `
                }} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="windrose-container" style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="windrose-error" style={{
                    padding: '20px',
                    border: '1px solid #dc3545',
                    borderRadius: '8px',
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    textAlign: 'center',
                    maxWidth: '300px'
                }}>
                    <h4 style={{ margin: '0 0 8px 0', color: '#721c24' }}>Windrose Error</h4>
                    <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>{error}</p>
                    <button
                        onClick={loadHourlyData}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // generateSimpleWindrose always returns one entry per compass point, so with
    // no usable records it returns SIXTEEN zero-count directions rather than an
    // empty array. Gating only on length left this branch unreachable and drew an
    // empty rose captioned "0 records" instead — so ask whether any direction
    // actually counted anything. This covers both a station with no observations
    // at all and one whose observations carry no wind, which is the same thing to
    // say to the reader.
    const hasAnyWind = windroseData.some(d => d.count > 0);
    if (windroseData.length === 0 || !hasAnyWind) {
        return (
            <div className="windrose-container" style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <p style={{ color: '#666', fontStyle: 'italic' }}>No wind data available for the last {hours} hours</p>
            </div>
        );
    }

    return (
        <div className="windrose-container" style={{ width, minHeight: height, display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', width: '100%', height: `${height}px`, flexShrink: 0 }}>
                <canvas
                    ref={canvasRef}
                    width={width}
                    height={height}
                    style={{ width: '100%', height: '100%' }}
                />
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#333',
                    border: '1px solid #ccc'
                }}>
                    <FontAwesomeIcon icon={faWind} style={{ fontSize: '20px', marginRight: '6px' }} />
                    {stationName || stationId} - Last {hours}h
                </div>
            </div>

            {/* Speed Legend */}
            <div className="speed-legend" style={{
                fontSize: '10px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px',
                justifyContent: 'center',
                marginTop: '8px',
                padding: '8px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                color: '#333'
            }}>
                {SPEED_RANGES.map(range => (
                    <div key={range.label} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            backgroundColor: range.color,
                            border: '1px solid #ccc'
                        }}></div>
                        <span style={{ color: '#333' }}>{range.label}</span>
                    </div>
                ))}
            </div>

            {metadata && (
                <div className="windrose-metadata" style={{
                    fontSize: '11px',
                    color: '#666',
                    marginTop: '4px',
                    textAlign: 'center'
                }}>
                    <div><FontAwesomeIcon icon={faChartBar} style={{ fontSize: '14px', marginRight: '4px' }} />{summary?.totalRecords ?? 0} records | <FontAwesomeIcon icon={faWind} style={{ fontSize: '14px', marginLeft: '8px', marginRight: '4px' }} />Avg: {summary?.avgWindSpeed ?? '0.00'} km/h</div>
                    <div><FontAwesomeIcon icon={faClock} style={{ fontSize: '14px', marginRight: '4px' }} />{new Date(metadata.from_date).toLocaleString()} - {new Date(metadata.to_date).toLocaleString()}</div>
                </div>
            )}
        </div>
    );
};

export default HourlyWindrose;